import React from "react";
import { useLang } from "@/i18n/LanguageContext";
import { Compass, Plane, Moon } from "lucide-react";

const DAY = 86400000;

function computePhase(startStr, endStr) {
  if (!startStr || !endStr) return null;
  const start = new Date(startStr + "T00:00:00");
  const end = new Date(endStr + "T00:00:00");
  if (isNaN(start) || isNaN(end)) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const total = Math.round((end - start) / DAY) + 1;
  if (today < start) {
    return { phase: "before", daysTo: Math.ceil((start - today) / DAY) };
  }
  if (today > end) {
    return { phase: "after" };
  }
  return { phase: "during", dayNumber: Math.round((today - start) / DAY) + 1, total };
}

export const JourneyProgress = ({ settings }) => {
  const { t, L } = useLang();
  const info = computePhase(settings?.tripStartDate, settings?.tripEndDate);
  if (!info) return null;

  const Icon = info.phase === "after" ? Moon : info.phase === "during" ? Plane : Compass;
  const overline =
    info.phase === "before" ? t("progress.before") : info.phase === "during" ? t("progress.during") : t("progress.after");

  return (
    <section data-testid="journey-progress" className="animate-fade-up">
      <div
        className="relative overflow-hidden rounded-[1.75rem] p-6 sm:p-7 text-white"
        style={{
          background: "linear-gradient(135deg, #14294d 0%, #0A192F 60%, #061224 100%)",
          boxShadow: "0 18px 44px -14px rgba(2,12,27,0.5)",
        }}
      >
        {/* soft gold glow */}
        <div
          className="pointer-events-none absolute -right-10 -top-12 h-40 w-40 rounded-full opacity-25 blur-2xl"
          style={{ background: "radial-gradient(circle, #D4AF37 0%, transparent 70%)" }}
        />

        <div className="relative flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#E9CE7D]">{overline}</p>

            {info.phase === "before" && (
              <>
                <h2 className="mt-2 font-display text-2xl font-bold leading-tight">{L(settings.tripDates)}</h2>
                <p className="mt-1 text-sm text-white/60">{L(settings.destination)}</p>
              </>
            )}

            {info.phase === "during" && (
              <>
                <h2 className="mt-2 font-display text-3xl font-bold leading-none">
                  {t("progress.day")} {info.dayNumber}
                  <span className="ml-1 text-lg font-medium text-white/55">
                    {t("progress.of")} {info.total}
                  </span>
                </h2>
                <p className="mt-1.5 text-sm text-white/70">{t("progress.underway")}</p>
              </>
            )}

            {info.phase === "after" && (
              <>
                <p className="mt-2 font-arabic text-2xl text-[#E9CE7D]">الحمد لله</p>
                <p className="mt-1 text-sm text-white/70">{t("progress.completed")}</p>
              </>
            )}
          </div>

          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-white/10 text-[#E9CE7D] ring-1 ring-white/15">
            <Icon size={22} strokeWidth={1.6} />
          </span>
        </div>

        {/* progress bar */}
        {info.phase === "during" && (
          <div className="relative mt-5">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/12">
              <div
                className="h-full rounded-full bg-gradient-to-r from-[#D4AF37] to-[#E9CE7D] transition-all duration-700"
                style={{ width: `${Math.min(100, (info.dayNumber / info.total) * 100)}%` }}
              />
            </div>
          </div>
        )}

        {info.phase === "before" && (
          <div className="relative mt-5 inline-flex items-center gap-2 rounded-full bg-white/10 px-3.5 py-1.5 ring-1 ring-white/12">
            <span className="h-1.5 w-1.5 rounded-full bg-[#E9CE7D]" />
            <span className="text-xs font-semibold text-white/85">
              {info.daysTo} {t("progress.daysToGo")}
            </span>
          </div>
        )}
      </div>
    </section>
  );
};
